import { Component, effect, Input, input, OnInit, signal, ChangeDetectorRef, NgZone } from '@angular/core';
import { firstValueFrom, merge } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ContainerSet,
  FileUpload,
  SidebarItemsDto,
  TableList,
  TableListItem,
  UUID,
  XSSheet,
} from '../../shell/shell.model';
import { TablesService } from 'src/app/core/service/table.service';
import { ToastService } from 'src/app/toast/toast.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContainerService } from 'src/app/core/service/container.service';
import {
  StructuringStatus,
  TableExtractorService,
} from 'src/app/core/service/table-extractor.service';
import {
  ActiveSheetObservable,
  StructureObservable,
  WorkspacesObservable,
} from 'src/app/core/service/observable.service';
import { LocalStorageService } from 'src/app/core/service/local-storage.service';
import { StructureEvent } from 'src/app/core/models/websocket/structure';
import { WorkspacesEvent } from './workspace-manager.events';

@Component({
  selector: 'app-workspace-manager',
  templateUrl: './workspace-manager.component.html',
  styleUrl: './workspace-manager.component.scss',
  standalone: false,
})
export class WorkspaceManagerComponent implements OnInit {
  @Input() toggleSidebar!: () => void;

  isExpanded = input<boolean>();
  isNewDataspaceCreated: boolean = true;
  _jsonSchema: string = '';
  _setupJsonSchema: boolean = false;
  files: FileUpload[] = [];
  filesCurrentlyUploading: number = 0;
  tableHasFile: boolean = false;
  files_unstructured_count: number = 0;
  loading: boolean = false;
  activeContainer: UUID | undefined = '';
  signedUrl: string = '';
  _fileMimeType: string = '';
  chats: any;
  schema_id: string = '';
  schema_name: string = '';
  activeSheet: XSSheet | undefined;
  suggestions: string[] = [];
  selected_file_index: number = 0;
  schemaIsDraft: boolean = false;
  containers: ContainerSet = [];
  tables: TableList = [];
  showTooltipFlag: boolean = false;
  structuringTableId: string = '';
  deleteId: string = '';
  spreadSheetLoading: boolean = false;
  cancelling: boolean = false;
  isEmptyState: boolean = false;
  showSearchInput: boolean = false;
  searchValue = signal('');
  dataspaces: TableListItem[] = [];
  private refreshSidebarInFlight: Promise<void> | null = null;
  private lastRefreshFailureAt = 0;
  private readonly refreshFailureCooldownMs = 10000;

  constructor(
    private tableService: TablesService,
    private containerService: ContainerService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private tableExtractor: TableExtractorService,
    private activeSheetObservable: ActiveSheetObservable,
    private structureObservable: StructureObservable,
    private workspacesObservable: WorkspacesObservable,
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private localStorageService: LocalStorageService
  ) {
    effect((onCleanup) => {
      //keep this line, so effect keep trigering on search value change
      this.searchValue();
      const timeout = setTimeout(() => {
        this.getContainers();
      }, 300);

      onCleanup(() => {
        clearTimeout(timeout);
      });
    });
  }

  ngOnInit(): void {
    merge(
      this.activeSheetObservable.tableId$,
      this.activeSheetObservable.tableName$
    ).subscribe(() => {
      this.refreshSidebar();
    });

    this.refreshSidebar().then(() => {
      this.checkStructuringStatus();

      
    });

    this.tableService.getUpdateNotification().subscribe((data: any) => {
      this.activeContainer = data.containerId;
      this.refreshSidebar();
    });
    this.subscribeToStructureObservable();
    this.subscribeToWorkspacesObservable();
  }

  subscribeToStructureObservable() {
    this.structureObservable
      .updates()
      .subscribe(async (data: StructureEvent) => {
        const matchesActiveTable = data.tableId === this.activeTable || (!data.tableId && !!this.activeTable);
        if (matchesActiveTable) {
          switch (data.status) {
            case 'all-start': {
              this.setTableLoading(this.activeTable, false);
              break;
            }
            case 'cancelled': {
              this.setTableLoading(this.activeTable, true);
              break;
            }
            case 'all-done': {
              this.setTableLoading(this.activeTable, true);
              break;
            }
          }
        }
      });
  }

  subscribeToWorkspacesObservable() {
    this.workspacesObservable
      .updates()
      .subscribe(async (data: WorkspacesEvent) => {
        if (data.type === 'change') {
          // Force refresh so document count updates after upload/delete even if a refresh is in flight
          await this.refreshSidebar(true);
          this.ngZone.run(() => {
            this.updateDataspacesCache();
            this.cdRef.detectChanges();
          });
        } else if (data.type === 'rename') {
          this.ngZone.run(() => {
            if (data.tableId === this.activeTable) {
              this.activeTableName = data.value || '';
            }
            this.tables.find(
              (table) => table.tableId === this.activeTable
            )!.tableName = this.activeTableName;
            this.cdRef.detectChanges();
          });
        }
      });
  }

  checkStructuringStatus(): void {
    this.tableExtractor.getStructuringStatus().subscribe({
      next: (status: StructuringStatus) => {
        if (status.isStructuring && status.tableId) {
          const tableId = status.tableId;

          if (tableId === this.activeTable || !this.activeTable) {
            this.setTableLoading(tableId, false);
          } else {
            this.setTableLoading(tableId, true);
          }

          console.log(
            `Resuming structuring process for table ${tableId} with ${status.jobCount} jobs`
          );
        }
      },
      error: (error) => {
        console.error('Error checking structuring status:', error);
      },
    });
  }

  setTableLoading(tableId: string, unset: boolean = false) {
    if (tableId) {
      // Check in main tables list
      let tableItemIndex = this.tables.findIndex(
        (table) => table.tableId == tableId
      );
      if (tableItemIndex > -1) {
        console.log('Toggle loading of table at index ', tableItemIndex);
        this.tables[tableItemIndex].loading = !unset;
        return;
      }

      this.containers.forEach((container) => {
        const table = container.tables.find((t) => t.tableId === tableId);
        if (table) {
          console.log(
            'Toggle loading of table in container ',
            container.containerName
          );
          table.loading = !unset;
        }
      });
    }
  }

  async getContainers() {
    const searchValue = this.searchValue();
    let res: SidebarItemsDto;

    if (this.isStringSearchable()) {
      res = await firstValueFrom(
        this.containerService.searchWorkspace(searchValue)
      );
    } else {
      res = await firstValueFrom(this.containerService.getContainers());
    }

    if (res && res.data) {
      this.containers = res.data.dataWithContainerId || [];
      this.tables = res.data.dataWithoutContainerId
        ? res.data.dataWithoutContainerId.tables
        : [];

      this.updateDataspacesCache();
      this.cdRef.detectChanges();
      return true;
    } else {
      return false;
    }
  }

  async refreshSidebar(force: boolean = false): Promise<void> {
    const now = Date.now();

    if (
      !force &&
      this.lastRefreshFailureAt &&
      now - this.lastRefreshFailureAt < this.refreshFailureCooldownMs
    ) {
      const remainingMs =
        this.refreshFailureCooldownMs - (now - this.lastRefreshFailureAt);
      console.warn(
        `Skipping sidebar refresh due to recent backend failure. Retrying in ${remainingMs}ms`
      );
      return;
    }

    if (this.refreshSidebarInFlight) {
      if (!force) {
        return this.refreshSidebarInFlight;
      }

      await this.refreshSidebarInFlight;
    }

    const refreshPromise = this.performSidebarRefresh();

    this.refreshSidebarInFlight = refreshPromise
      .then(() => {
        this.lastRefreshFailureAt = 0;
      })
      .catch((error) => {
        this.lastRefreshFailureAt = Date.now();
        throw error;
      })
      .finally(() => {
        this.refreshSidebarInFlight = null;
      });

    return this.refreshSidebarInFlight;
  }

  private async performSidebarRefresh(): Promise<void> {
    try {
      const isSuccessful = await this.getContainers();

      if (!isSuccessful) {
        this.resetSidebarState();
      }
    } catch (err) {
      console.log('Error when refreshing sidebar', err);
      this.resetSidebarState();
      throw err;
    }
  }

  private resetSidebarState(): void {
    this.containers = [];
    this.tables = [];
    this.activeTable = '';
    this.activeTableName = '';
    this.activeContainer = '';
    this.chats = '';
  }

  async newWorkspaceOrDataset() {
    this.activeTable = '';
  }

  searchWorkspace() {
    this.showSearchInput = true;

    if (this.showSearchInput) {
      setTimeout(() => {
        const input = document.querySelector(
          '.search-wrapper input'
        ) as HTMLInputElement;
        if (input) input.focus();
      }, 0);
    }
  }
  // private setupSearch(): void {
  //     this.searchSubscription = this.searchText$.pipe(
  //       debounceTime(400),
  //       distinctUntilChanged(),
  //       tap((phrase) => {
  //         if (phrase.length < 2) {
  //           this.setItems(this.initialItems());
  //         }
  //       }),
  //       filter((phrase) => {
  //         return !!this.activeTableId() && phrase.length >= 2;
  //       }),
  //       switchMap((search) => {
  //         return this.tablesService.searchFiles(this.activeTableId(), search);
  //       })
  //     ).subscribe((items) => {
  //       this.setItems(items);
  //     });
  //   }

  closeSearch() {
    this.showSearchInput = false;
  }

  onSearchChange(event: Event) {
    const inputElement = event.target as HTMLInputElement;

    this.searchValue.set(inputElement.value);
  }

  showTooltip() {
    this.showTooltipFlag = true;
  }

  hideTooltip() {
    this.showTooltipFlag = false;
  }

  getDataspaces(nCount: number = Infinity) {
    if (this.isStringSearchable()) {
      return this.tables
        .filter((table) => table.type == 'dataspace')
        .sort((a, b) => b.modifiedOn - a.modifiedOn)
        .slice(0, nCount);
    }

    const filteredDataspaces = this.tables.filter(
      (table) => table.type == 'dataspace'
    );
    
    return filteredDataspaces.slice(0, nCount);
  }

  getDataspaceCount() {
    return this.tables.filter((table) => table.type == 'dataspace').length;
  }

  private updateDataspacesCache() {
    const filteredDataspaces = this.tables.filter(
      (table) => table.type == 'dataspace'
    );

    if (
      this.dataspaces.length !== filteredDataspaces.length ||
      !filteredDataspaces.every((ds) =>
        this.dataspaces.some((cached) => cached.tableId === ds.tableId)
      )
    ) {
      this.dataspaces = [...filteredDataspaces];
    }
  }

  onDataspacesDrop(event: CdkDragDrop<TableListItem[]>) {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.dataspaces, event.previousIndex, event.currentIndex);
      this.updateTableOrder();
    }
  }

  trackDataspaceById(index: number, item: TableListItem): string {
    return item.tableId || index.toString();
  }

  private updateTableOrder() {
    const tableIds = this.dataspaces
      .map((dataspace) => dataspace.tableId)
      .filter((id) => id !== undefined) as string[];

    this.containerService.updateTableOrder(tableIds).subscribe({
      next: (response: any) => {
        this.toastService.show(
          'success',
          'Dataspace order updated successfully',
          {
            classname:
              'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
          }
        );

        this.refreshSidebar();
      },
      error: (error) => {
        console.error('Error updating table order:', error);
        this.toastService.show('error', 'Failed to update dataspace order', {
          classname:
            'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
        });
        this.refreshSidebar();
      },
    });
  }

  getSpreadSheet(table: TableListItem) {
    this.activeTable = table.tableId || '';
  }

  onRenameKeydown(event: KeyboardEvent, table: any) {
    if (event.key === 'Enter') {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
      this.tableRename(table);
    }
  }

  tableRename(table: any) {
    if (table.isRenaming) {
      return;
    }

    table.isRenaming = true;
    table.tableName = table.editedName;

    this.tableService
      .updateTable(table.tableId, { tableName: table.editedName })
      .subscribe((res: any) => {
        table.isNameEdit = false;
        table.isRenaming = false;
        this.tableService.setUpdateFromSpreadsheet(table);
        this.toastService.show('info', 'Dataspace name successfully updated', {
          classname:
            'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
        });
      });
  }

  duplicateTable(table: any) {
    this.tableService
      .duplicateTable(table.tableId, table)
      .subscribe((res: any) => {
        this.refreshSidebar();
        this.toastService.show('info', 'Table is duplicated successfully.', {
          classname:
            'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
        });
      });
  }

  confirm(content: any, id: any) {
    this.deleteId = id;
    this.modalService.open(content, { centered: true });
  }

  openEmptyState() {
    this.isEmptyState = true;
  }

  deleteTable(deleteId: string) {
    this.tableService.deleteTable(deleteId).subscribe((res: any) => {
      this.localStorageService.clearInterruptedUploadForTable(deleteId);
      this.toastService.show('info', 'Table is deleted.', {
        classname: 'bg-white text-dark position-fixed bottom-0 end-0 mb-7 me-3',
      });
      window.location.reload();
    });
  }

  isStringSearchable() {
    return this.searchValue().length >= 2;
  }

  isAnyDataspaceBeingRenamed() {
    return this.dataspaces.some((dataspace) => dataspace.isNameEdit);
  }

  startRename(table: any, dropdown: any) {
    table.isNameEdit = !table.isNameEdit;
    table.editedName = table.tableName;
    dropdown.close();
  }

  get activeTable() {
    return this.activeSheetObservable.getActiveTable();
  }

  set activeTable(value: string) {
    this.activeSheetObservable.setActiveTable(value);
  }

  get activeTableName(): string {
    return this.activeSheetObservable.getActiveTableName();
  }

  set activeTableName(name: string) {
    this.activeSheetObservable.setActiveTableName(name);
  }
}
